/**
 * CSV Parser utility for importing contacts
 */

export interface ParsedContact {
  firstName: string
  lastName: string
  email: string
  phone: string
  rowNumber: number
  errors: string[]
}

export interface CSVParseResult {
  contacts: ParsedContact[]
  headers: string[]
  validCount: number
  errorCount: number
  totalRows: number
}

/**
 * Parse CSV file and extract contact data
 */
export const parseCSV = (csvText: string): CSVParseResult => {
  const lines = csvText.split('\n').filter(line => line.trim())
  
  if (lines.length === 0) {
    throw new Error('CSV file is empty')
  }

  // Parse headers
  const headers = parseCSVLine(lines[0])
  
  // Try to auto-detect column mapping
  const columnMap = detectColumnMapping(headers)
  
  const contacts: ParsedContact[] = []
  let validCount = 0
  let errorCount = 0

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i])
    const errors: string[] = []
    
    // Extract fields based on column mapping
    const firstName = getFieldValue(values, columnMap.firstName, headers)
    const lastName = getFieldValue(values, columnMap.lastName, headers)
    const email = getFieldValue(values, columnMap.email, headers)
    const phone = getFieldValue(values, columnMap.phone, headers)

    // Validate required fields
    if (!firstName && !lastName) {
      errors.push('First name or last name is required')
    }
    
    if (!email && !phone) {
      errors.push('Email or phone is required')
    }

    // Validate email format
    if (email && !isValidEmail(email)) {
      errors.push(`Invalid email: ${email}`)
    }

    // Validate phone format (basic)
    if (phone && !isValidPhone(phone)) {
      errors.push(`Invalid phone: ${phone}`)
    }

    if (errors.length === 0) {
      validCount++
    } else {
      errorCount++
    }

    contacts.push({
      firstName: firstName || '',
      lastName: lastName || '',
      email: email || '',
      phone: phone || '',
      rowNumber: i + 1,
      errors,
    })
  }

  return {
    contacts,
    headers,
    validCount,
    errorCount,
    totalRows: lines.length - 1,
  }
}

/**
 * Parse a single CSV line, handling quoted fields
 */
const parseCSVLine = (line: string): string[] => {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    const nextChar = line[i + 1]

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"'
        i++ // Skip next quote
      } else {
        inQuotes = !inQuotes
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }

  result.push(current.trim())
  return result
}

/**
 * Auto-detect column mapping from headers
 */
const detectColumnMapping = (headers: string[]): Record<string, number> => {
  const map: Record<string, number> = {
    firstName: -1,
    lastName: -1,
    email: -1,
    phone: -1,
  }

  headers.forEach((header, index) => {
    const normalized = header.toLowerCase().trim()
    
    // First name detection
    if (normalized.includes('first') && normalized.includes('name')) {
      map.firstName = index
    } else if (normalized === 'firstname' || normalized === 'nombre') {
      map.firstName = index
    }
    
    // Last name detection
    if (normalized.includes('last') && normalized.includes('name')) {
      map.lastName = index
    } else if (normalized === 'lastname' || normalized === 'apellido') {
      map.lastName = index
    }
    
    // Email detection
    if (normalized.includes('email') || normalized.includes('correo')) {
      map.email = index
    }
    
    // Phone detection
    if (normalized.includes('phone') || normalized.includes('telefono') || normalized.includes('cel')) {
      map.phone = index
    }
  })

  return map
}

/**
 * Get field value from row by column index
 */
const getFieldValue = (values: string[], columnIndex: number, headers: string[]): string => {
  if (columnIndex === -1 || columnIndex >= values.length) {
    return ''
  }
  return values[columnIndex].trim()
}

/**
 * Validate email format
 */
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate phone format (basic validation)
 */
const isValidPhone = (phone: string): boolean => {
  // Remove common formatting characters
  const cleaned = phone.replace(/[\s\-\(\)]/g, '')
  // Check if it's a valid phone number (at least 10 digits)
  return /^\+?[\d]{10,}$/.test(cleaned)
}

/**
 * Convert parsed contacts to GraphQL input format
 */
export const contactsToGraphQLInput = (contacts: ParsedContact[]) => {
  return contacts
    .filter(c => c.errors.length === 0)
    .map(c => ({
      firstName: c.firstName,
      lastName: c.lastName,
      email: c.email || undefined,
      phone: c.phone || undefined,
    }))
}
